import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CurrencyPipe, Location, TitleCasePipe } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { uniq } from 'lodash';
import { map, tap } from 'rxjs/operators';
import { intersection, orderBy, unescape } from 'lodash';
import { ILcdVideoWall, ITaxonomy } from '../../global/lcd-video-wall';

const roundNum = (num, roundTo) => {
  return Math.round(num / roundTo)*roundTo;
}

interface IFilter {
  name: string;
  value: (args: any) => (string|number)[];
  description: string;
  multiple: boolean;
  options: (string|number)[];
  selected: BehaviorSubject<(string|number)[]>;
  default?: (args: (string|number)[]) => (string|number)[];
  label?: any;
  hook?: (...args: any[]) => void;
}

interface ISort {
  orderParams: () => string[];
  label: string;
}

@Component({
  selector: 'app-lcd-video-walls',
  templateUrl: './lcd-video-walls.component.html',
  styleUrls: ['./lcd-video-walls.component.scss']
})
export class LcdVideoWallsComponent implements OnInit {

  @ViewChild('viewer') viewer: ElementRef<HTMLDivElement>;


  industry: ITaxonomy[] = (window as any).TAXONOMY_DATA.industry;
  lcd_video_wall_style: ITaxonomy[] = (window as any).TAXONOMY_DATA.lcd_video_wall_style;

  priceMode: 'lease_36' | 'purchase' = 'lease_36';
  money: 'usd' | 'cad' = 'cad';

  showClass = '';
  viewMode = 'grid';

  sortTypes: ISort[] = [
    {
      orderParams: () => ['name', 'asc'],
      label: 'Name (ascending)',
    },
    {
      orderParams:   () => ['name', 'desc'],
      label: 'Name (descending)',
    },
    {
      orderParams:     () => ['prices.purchase_' + this.money, 'asc'],
      label: 'Purchase Price (Low to High)',
    },
    {
      orderParams:       () => ['prices.purchase_' + this.money, 'desc'],
      label: 'Purchase Price (High to Low)',
    },
    {
      orderParams:         () => ['prices.lease_36_' + this.money, 'asc'],
      label: 'Lease Rate (Low to High)',
    },
    {
      orderParams:           () => ['prices.lease_36_' + this.money, 'desc'],
      label: 'Lease Rate (High to Low)',
    },
  ]

  sortBy$: BehaviorSubject<ISort> = new BehaviorSubject(this.sortTypes[0]);

  filters: IFilter[] = [
    {
      name: 'Orientation',
      value: (vw: ILcdVideoWall) => [vw.orientation],
      multiple: true,
      description: 'Select if you would like video wall panels arranged horizontally or vertically.',
      options: [],
      selected: new BehaviorSubject([]),
    },
    {
      name: 'Wall Width',
      value: (vw: ILcdVideoWall) => [vw.wall_width],
      multiple: false,
      description: 'Filter video walls by the available wall width.',
      options: [],
      selected: new BehaviorSubject([]),
      default: () => [],
    },
    {
      name: 'Ceiling Height',
      value: (vw: ILcdVideoWall) => [vw.ceiling_height],
      multiple: false,
      description: 'Filter video walls by the available ceiling height.',
      options: [],
      selected: new BehaviorSubject([]),
      default: () => [],
    },
    {
      name: 'Styles',
      value: (vw: ILcdVideoWall) => vw.styles.map(id => unescape(this.lcd_video_wall_style.find(tax => tax.id === id)?.name)),
      multiple: true,
      description: 'Whether you want a simple video wall solution or an extravagant Las Vegas-style video wall we have the solution for you. Browse our variety of video wall styles below.',
      options: [],
      selected: new BehaviorSubject([]),
    },
    {
      name: 'Industry',
      value: (vw: ILcdVideoWall) => vw.industries.map(id => unescape(this.industry.find(tax => tax.id === id)?.name)),
      multiple: true,
      description: 'Eflyn portfolio has more than 400+ video wall bundles. Our well experienced engineers have grouped these bundles into various vertical markets. Please select from below.',
      options: [],
      selected: new BehaviorSubject([]),
    },
    {
      name: 'Purchase Budget',
      value: (vw: ILcdVideoWall) => {
        const rounded = roundNum(parseInt(vw.prices ? vw.prices['purchase_' + this.money] : '0'), 2500);
        console.log('rounded is' ,rounded);
        const finalAmount = Math.max(rounded, 2500);
        return [
          Math.round(finalAmount)
        ]
      },
      label: (option: string, ind: number, options: string[]) => {
        return `${ind === 0 ? 'Under ' : ''}${ (ind > 0 && ind < options.length - 1) ? ('') + this.currency.transform(options[ind - 1], 'USD', 'symbol','3.0') : '' }${ ind > 0 && ind < options.length - 1 ? ' to ' : '' }${this.currency.transform(option, 'USD', 'symbol','3.0')}${ ind === options.length - 1 ? ' and more' : ''}`;
      },
      hook: () => {
        this.priceMode = 'purchase';
      },
      multiple: true,
      description: 'Select the budget your company has for your outstanding world class video wall bundle.',
      options: [],
      selected: new BehaviorSubject([]),
    },
    {
      name: 'Lease Budget',
      value: (vw: ILcdVideoWall) => {
        const rounded = roundNum(parseInt(vw.prices ? vw.prices['lease_36_' + this.money] : '0'), 100);
        const finalAmount = Math.max(rounded, 100);
        return [
          Math.round(finalAmount)
        ]
      },
      label: (option: string, ind: number, options: string[]) => {
        return `${ind === 0 ? 'Under ' : ''}${ (ind > 0 && ind < options.length - 1) ? ('') + this.currency.transform(options[ind - 1], 'USD', 'symbol','3.0') : '' }${ ind > 0 && ind < options.length - 1 ? ' to ' : '' }${this.currency.transform(option, 'USD', 'symbol','3.0')}${ ind === options.length - 1 ? ' and more' : ''}`;
      },
      hook: () => {
        this.priceMode = 'lease_36';
      },
      multiple: true,
      description: 'Select the budget your company has for your outstanding world class video wall bundle.',
      options: [],
      selected: new BehaviorSubject([]),
    }
  ];

  products$: BehaviorSubject<ILcdVideoWall[]> = new BehaviorSubject((window as any).CATALOG_DATA);
  filterState$: Observable<IFilter[]>;
  filterStateSub: Subscription;


  filteredProducts$: BehaviorSubject<ILcdVideoWall[]> = new BehaviorSubject<ILcdVideoWall[]>([]);


  constructor(
    private location$: Location,
    private titleCase: TitleCasePipe,
    private currency: CurrencyPipe,
  ) {
    console.log('on video wall');
    this.loadFilterState();
    this.filterStateSub = this.filterState$.subscribe();

    this.getQueryParams();
  }

  getQueryParams() {
    // const search = new URLSearchParams(window.location.search);
    // this.selectedStyle$.next(
    //   search.has('style') ? decodeURIComponent(search.get('style')).split('|') : []
    // );
    // this.selectedOrientation$.next(
    //   search.has('orientation') ? decodeURIComponent(search.get('orientation')).split('|') : []
    // );
    // this.selectedWallWidth$.next(
    //   search.has('wall_width') ? decodeURIComponent(search.get('wall_width')).split('|') : []
    // );
    // this.selectedCeilingHeight$.next(
    //   search.has('ceiling_height') ? decodeURIComponent(search.get('ceiling_height')).split('|') : []
    // );
  }

  setQueryParams(style, orientation, wall_width, ceiling_height) {
    // console.log('style is', style);
    // this.location$.replaceState(this.location$.path().split('?')[0], [
    //   style.length ? `style=${encodeURIComponent((style).join('|'))}` : '',
    //   orientation.length ? `orientation=${encodeURIComponent((orientation).join('|'))}` : '',
    //   wall_width.length ? `wall_width=${encodeURIComponent((wall_width).join('|'))}` : '',
    //   ceiling_height.length ? `ceiling_height=${encodeURIComponent((ceiling_height).join('|'))}` : '',
    // ].filter(s => s).join('&'));
  }

  ngOnInit(): void {
  }

  setDefaults() {
    for (const filter of this.filters) {
      const vals = filter.options;
      if (filter.default) {
        filter.selected.next(filter.default(vals));
      } else {
        filter.selected.next([]);
      }
    }
  }

  loadFilterState() {
    for (const prod of this.products$.value) {
      for (const filter of this.filters) {
        const vals = filter.value(prod);
        for (const val of vals) {
          if (val && filter.options.indexOf(val) === -1) {
            filter.options.push(val);
          }
        }
        filter.options = orderBy(filter.options);
        // filter.options = orderBy(filter.options, (option: string) => {
        //   // Is number
        //   if (option.slice(0, 1) === '$') {
        //     return parseInt(option.slice(1).replace(/,/g, ''), 10)
        //   }
        //   return option;
        // });
      }
    }
    this.setDefaults();
    this.filterState$ = combineLatest([this.sortBy$, ...this.filters.map(f => f.selected)])
      .pipe(
        map(selectResults => {
          return selectResults.slice(1).map((_, ind) => this.filters[ind]);
        }),
        tap((filterState: IFilter[]) => {
          let products = [];

          for (const prod of this.products$.value) {
            let include = true;
            for (const filter of filterState) {
              if (filter.selected.value.length > 0) {
                const productValues = filter.value(prod);
                console.log('product values are', productValues);
                include = include && intersection(productValues, filter.selected.value).length > 0;
              }
            }
            if (include) {
              products.push(prod);
            }
          }
          console.log('ordering by', ...this.sortBy$.value.orderParams());
          products = orderBy(products, ...this.sortBy$.value.orderParams());
          console.log('next products', products);
          this.filteredProducts$.next(products);
          setTimeout(() => {
            this.scrollTop();
          }, 150);
        })
      )
  }

  toggleFilter(filter: IFilter, option: string) {
    const optionInd = filter.selected.value.indexOf(option);
    if (filter.multiple) {
      if (filter.selected.value.includes(option)) {
        filter.selected.next([
          ...filter.selected.value.slice(0, optionInd),
          ...filter.selected.value.slice(optionInd + 1),
        ]);
      } else {
        filter.selected.next([
          ...filter.selected.value,
          option
        ]);
      }
    } else {
      if (filter.selected.value.includes(option)) {
        filter.selected.next([]);
      } else {
        filter.selected.next([option]);
      }
    }
    if (filter.hook) {
      filter.hook();
    }
  }


  scrollTop() {
    window.scrollTo({ top: 0, left: 0 });
  }


  resetFilters() {
    this.setDefaults();
  }
}
